import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Loader2, Save } from 'lucide-react';
import { useToast } from '../components/Toast';
import { HelpIcon } from '../components/HelpIcon';
import { apiClient } from '../utils/api';
import {
  createTask,
  getTask,
  TASK_STATUS_LABELS,
  TASK_TYPE_LABELS,
  TaskStatus,
  TaskType,
  updateTask,
} from '../services';
import { DateInput } from '../components/DateInput';

interface UserOption {
  id: string;
  email: string;
  userType: string;
  displayName: string;
}

const toInputDate = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const toIsoOrNull = (v: string) => {
  if (!v) return null;
  return new Date(v).toISOString();
};

export function TaskFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();

  const [users, setUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingTask, setLoadingTask] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    title: '',
    taskType: 'CUSTOM' as TaskType,
    description: '',
    status: 'PENDING' as TaskStatus,
    startDate: '',
    endDate: '',
    effectiveEndDate: '',
    url: '',
    assignedToId: '',
  });

  useEffect(() => {
    loadUsers();
    if (isEditing && id) {
      loadTask(id);
    }
  }, [id]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const [adminsRes, practitionersRes] = await Promise.all([
        apiClient('/api/admin/admins?limit=1000'),
        apiClient('/api/admin/practitioners?limit=1000'),
      ]);
      const [adminsData, practitionersData] = await Promise.all([
        adminsRes.json(),
        practitionersRes.json(),
      ]);

      const allUsers: UserOption[] = [];

      if (adminsData.success) {
        for (const a of adminsData.data?.admins ?? []) {
          if (a.userId) {
            allUsers.push({
              id: a.userId,
              email: a.email,
              userType: 'ADMIN',
              displayName: `${a.name || a.email} (Admin)`,
            });
          }
        }
      }

      if (practitionersData.success) {
        for (const p of practitionersData.data?.practitioners ?? []) {
          if (p.userId) {
            allUsers.push({
              id: p.userId,
              email: p.email,
              userType: 'PRACTITIONER',
              displayName: `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.email + ' (Praticien)',
            });
          }
        }
      }

      setUsers(allUsers);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadTask = async (taskId: string) => {
    setLoadingTask(true);
    try {
      const task = await getTask(taskId);
      setForm({
        title: task.title,
        taskType: task.taskType,
        description: task.description ?? '',
        status: task.status,
        startDate: toInputDate(task.startDate),
        endDate: toInputDate(task.endDate),
        effectiveEndDate: toInputDate(task.effectiveEndDate),
        url: task.url ?? '',
        assignedToId: task.assignedToId,
      });
    } catch (err: any) {
      setError(err.message || 'Impossible de charger la tâche');
    } finally {
      setLoadingTask(false);
    }
  };

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      showToast('Le titre est requis', 'error');
      return;
    }
    if (!form.assignedToId) {
      showToast('Veuillez sélectionner un assigné', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        taskType: form.taskType,
        description: form.description.trim() || null,
        status: form.status,
        startDate: toIsoOrNull(form.startDate),
        endDate: toIsoOrNull(form.endDate),
        effectiveEndDate: toIsoOrNull(form.effectiveEndDate),
        url: form.url.trim() || null,
        assignedToId: form.assignedToId,
      };

      if (isEditing && id) {
        await updateTask(id, payload);
        showToast('Tâche mise à jour', 'success');
      } else {
        await createTask(payload);
        showToast('Tâche créée', 'success');
      }
      navigate('/tasks');
    } catch (err: any) {
      showToast(err.message || 'Erreur lors de la sauvegarde', 'error', 7000);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingTask) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => navigate('/tasks')}
          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center gap-2">
            {isEditing ? 'Modifier la tâche' : 'Nouvelle tâche'}
            <HelpIcon
              docPath={isEditing ? '17-gestion-taches#17-4-modification' : '17-gestion-taches#17-3-creation'}
              tooltip="Documentation : Gestion des tâches"
            />
          </h1>
        </div>
      </div>

      <ToastComponent />

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Titre <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Ex : Assigner un perceur au RDV du 01/04"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            required
          />
        </div>

        {/* Type & Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type de tâche</label>
            <select
              value={form.taskType}
              onChange={(e) => handleChange('taskType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            >
              {(Object.entries(TASK_TYPE_LABELS) as [TaskType, string][]).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
            <select
              value={form.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            >
              {(Object.entries(TASK_STATUS_LABELS) as [TaskStatus, string][]).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={4}
            placeholder="Décrivez la tâche en détail..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 resize-none"
          />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
            <DateInput
              value={form.startDate}
              onChange={(value) => handleChange('startDate', value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date d'échéance</label>
            <DateInput
              value={form.endDate}
              onChange={(value) => handleChange('endDate', value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin réelle</label>
            <DateInput
              value={form.effectiveEndDate}
              onChange={(value) => handleChange('effectiveEndDate', value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Assigned to */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Assigné à <span className="text-red-500">*</span>
          </label>
          {loadingUsers ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Chargement des utilisateurs...
            </div>
          ) : (
            <select
              value={form.assignedToId}
              onChange={(e) => handleChange('assignedToId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
              required
            >
              <option value="">-- Sélectionner un utilisateur --</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName} — {u.email}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Lien vers la page de la tâche
          </label>
          <input
            type="url"
            value={form.url}
            onChange={(e) => handleChange('url', e.target.value)}
            placeholder="https://... ou /appointments/xxx"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            URL de l'application où l'utilisateur peut exécuter cette tâche.
          </p>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/tasks')}
            className="btn-secondary"
            disabled={submitting}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="btn-primary flex items-center gap-2"
            disabled={submitting}
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEditing ? 'Enregistrer' : 'Créer la tâche'}
          </button>
        </div>
      </form>
    </div>
  );
}
